import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareButton from 'legacyfrontend/components/ui/ShareButton';
import useWhatsAppUrl from 'legacyfrontend/hooks/useWhatsAppUrl';
import {
  FIXED_DETAIL_TAB_IDS,
  MAX_DETAIL_IMAGES,
  buildDetailStateFromBanner,
  getOrderedDetailTabs,
  mapItinerarioFromApi,
  stripHtml,
  tabHasData,
} from '../model/detailModel';
import { getIconThemeTileStyle, normalizeIconTheme } from '../model/iconTheme';
import PreviewDescription from './PreviewDescription';
import { PreviewMailIcon, PreviewTabIcon, PreviewWhatsappIcon } from './PreviewIcons';
import '../styles/banners-v1.css';

type BannerV1DetailProps = {
  banner: Record<string, unknown>;
  shareUrl: string;
  bannerRefNumber: string;
  onBack: () => void;
  onEmailClick: () => void;
};

export default function BannerV1Detail({
  banner,
  shareUrl,
  bannerRefNumber,
  onBack,
  onEmailClick,
}: BannerV1DetailProps) {
  const { t } = useTranslation(['banners', 'layout']);
  const productName = String(banner.titulo || '');
  const { whatsappUrl } = useWhatsAppUrl(null, productName, bannerRefNumber);

  const detail = buildDetailStateFromBanner(banner);
  const images = detail.images.slice(0, MAX_DETAIL_IMAGES);
  const orderedTabs = getOrderedDetailTabs(detail.movableOrder);
  const contentTabs = orderedTabs.filter(
    (tab) => !FIXED_DETAIL_TAB_IDS.includes(tab.id) && tabHasData(tab, detail),
  );
  const itinerarioItems =
    ((banner.itinerarioBanners as unknown[])?.length
      ? mapItinerarioFromApi(
          banner.itinerarioBanners as Array<{
            id: number;
            titulo?: string;
            descripcion?: string;
            orden?: number;
          }>,
        )
      : detail.data.itinerario) || [];
  const iconTheme = normalizeIconTheme(banner);
  const iconThemeStyle = getIconThemeTileStyle(iconTheme.mode, iconTheme.color);
  const tagColors = {
    bg: (banner.backgroundTag as string) || '#2554c7',
    color: (banner.colorTag as string) || '#ffffff',
  };
  const tags = [banner.tag1, banner.tag2, banner.tag3]
    .filter((text) => text && String(text).trim())
    .map((text) => ({ text: String(text).trim() }));

  const [carouselIndex, setCarouselIndex] = useState(0);
  const [openCardId, setOpenCardId] = useState<string | null>(null);

  const safeIndex = images.length ? Math.min(carouselIndex, images.length - 1) : 0;
  const hasDescription = Boolean(stripHtml(detail.data.descripcion).trim());

  useEffect(() => {
    setCarouselIndex(0);
  }, [banner.id]);

  function toggleCard(id: string) {
    setOpenCardId((prev) => (prev === id ? null : id));
  }

  return (
    <div className='mb-banner-v1-detail'>
      <div className='preview-page'>
        <div className='banner__header p-4'>
          <h3 className='text-lg flex items-center'>
            <IconButton
              onClick={onBack}
              sx={{
                backgroundColor: 'white',
                width: '2rem',
                height: '2rem',
                marginRight: { xs: 'auto', sm: '0.5rem' },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <span>{String(banner.titulo || '')}</span>
            <ShareButton
              className='ml-2'
              title={t('banners:v1.shareTitle')}
              url={shareUrl}
              variant='banner'
            />
          </h3>
          {bannerRefNumber ? (
            <div className='banner-details__ref-number pl-10 pr-4'>
              <p>
                {t('banners:v1.refNumber')}: {bannerRefNumber}
              </p>
              <IconButton
                onClick={() => {
                  navigator.clipboard.writeText(bannerRefNumber);
                  alert(t('layout:common.link_copied'));
                }}
                sx={{
                  width: 24,
                  height: 24,
                  padding: '4px',
                  marginLeft: '8px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </div>
          ) : null}
        </div>

        <div className='preview-layout'>
          <div className='preview-gallery'>
            <div className='carousel-stage'>
              <h2 className='preview-banner-title'>{String(banner.titulo || '')}</h2>
              {images.length > 0 ? (
                <div className='carousel-media'>
                  <button
                    aria-label={t('banners:v1.prevImage')}
                    className='carousel-arrow prev'
                    disabled={images.length < 2}
                    onClick={() =>
                      setCarouselIndex((safeIndex - 1 + images.length) % images.length)
                    }
                    type='button'
                  >
                    ‹
                  </button>
                  <img
                    alt={images[safeIndex].name || `Imagen ${safeIndex + 1}`}
                    src={images[safeIndex].src}
                  />
                  <button
                    aria-label={t('banners:v1.nextImage')}
                    className='carousel-arrow next'
                    disabled={images.length < 2}
                    onClick={() => setCarouselIndex((safeIndex + 1) % images.length)}
                    type='button'
                  >
                    ›
                  </button>
                </div>
              ) : null}
            </div>
            {images.length > 0 ? (
              <div className='carousel-thumbs'>
                {images.map((image, index) => (
                  <button
                    className={['carousel-thumb', index === safeIndex ? 'active' : '']
                      .filter(Boolean)
                      .join(' ')}
                    key={index}
                    onClick={() => setCarouselIndex(index)}
                    type='button'
                  >
                    <img alt={image.name || `Imagen ${index + 1}`} src={image.src} />
                  </button>
                ))}
              </div>
            ) : null}
            {hasDescription ? <PreviewDescription html={String(detail.data.descripcion)} /> : null}
            {tags.length > 0 ? (
              <div className='preview-description-tags'>
                {tags.map((tag, index) => (
                  <span
                    className='preview-description-tag'
                    key={index}
                    style={{
                      background: tagColors.bg,
                      color: tagColors.color,
                    }}
                  >
                    {tag.text}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className='preview-data-column'>
            <div className='preview-accordion ql-editor'>
              {contentTabs.map((tab, index) => {
                const isOpen = openCardId === tab.id;
                return (
                  <section
                    className={['preview-accordion-card', isOpen ? 'open' : '']
                      .filter(Boolean)
                      .join(' ')}
                    key={tab.id}
                  >
                    <button
                      aria-expanded={isOpen}
                      className='preview-accordion-trigger'
                      onClick={() => toggleCard(tab.id)}
                      type='button'
                    >
                      <span
                        className={`preview-card-icon icon-${tab.id}`}
                        style={iconThemeStyle || undefined}
                      >
                        <PreviewTabIcon tabId={tab.id} />
                      </span>
                      <span className='preview-card-copy'>
                        <strong>
                          {index + 1}. {t(tab.nameKey)}
                        </strong>
                      </span>
                      <span className='preview-card-chevron'>›</span>
                    </button>
                    <div className='preview-accordion-body'>
                      {tab.id === 'imagenes' ? (
                        <div className='preview-image-grid'>
                          {detail.images.slice(0, MAX_DETAIL_IMAGES).map((image, imgIndex) => (
                            <img
                              alt={image.name || `Imagen ${imgIndex + 1}`}
                              key={imgIndex}
                              src={image.src}
                            />
                          ))}
                        </div>
                      ) : tab.id === 'itinerario' ? (
                        itinerarioItems
                          .slice()
                          .sort((a, b) => Number(a.order) - Number(b.order))
                          .filter((item) => item.title.trim() || stripHtml(item.description).trim())
                          .map((item) => (
                            <div className='preview-itinerary-item' key={item.id}>
                              <div className='preview-itinerary-number'>{item.order}</div>
                              <div>
                                <div className='preview-itinerary-title'>
                                  {item.title || t('banners:v1.itineraryUntitled')}
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: item.description || '' }} />
                              </div>
                            </div>
                          ))
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{ __html: String(detail.data[tab.id] || '') }}
                        />
                      )}
                    </div>
                  </section>
                );
              })}

              <section className='preview-accordion-card open preview-contact-fixed'>
                <div className='preview-accordion-trigger preview-accordion-static'>
                  <span
                    className='preview-card-icon icon-contacto'
                    style={iconThemeStyle || undefined}
                  >
                    <PreviewTabIcon tabId='contacto' />
                  </span>
                  <span className='preview-card-copy'>
                    <strong>{t('banners:v1.contactTitle')}</strong>
                  </span>
                  <span aria-hidden='true' />
                </div>
                <div className='preview-accordion-body'>
                  <div className='preview-contact-actions'>
                    <a
                      aria-label={t('banners:v1.contactMailAria')}
                      className='preview-contact-action mail'
                      href='#'
                      onClick={(event) => {
                        event.preventDefault();
                        onEmailClick();
                      }}
                    >
                      <PreviewMailIcon />
                      <span>{t('banners:v1.contactMail')}</span>
                    </a>
                    <a
                      aria-disabled={!whatsappUrl}
                      aria-label={t('banners:v1.contactWhatsappAria')}
                      className='preview-contact-action whatsapp'
                      href={whatsappUrl || '#'}
                      onClick={(event) => {
                        if (!whatsappUrl) event.preventDefault();
                      }}
                      rel={whatsappUrl ? 'noopener noreferrer' : undefined}
                      target={whatsappUrl ? '_blank' : undefined}
                    >
                      <PreviewWhatsappIcon />
                      <span>{t('banners:v1.contactWhatsapp')}</span>
                    </a>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
